import { useEffect, useMemo } from 'react'

import { authorByIdQuery } from '@/entities/authors'
import { UploadAvatar, type UploadFileWithId } from '@/features/upload-files'
import { confirm } from '@/shared/lib/antd-utils'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { PrimaryButton, SelfValidateInput } from '@/shared/ui'
import { SelfValidateTextArea } from '@/shared/ui'
import { Button, Flex, Form, Modal } from 'antd'
import { useGate, useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { getAuthorRequestByFormValues } from './lib/author-form-get-request'
import { MAX_FIELD_LENGTH } from './lib/constants'
import {
  $isAuthorFormModalOpened,
  $submittingAuthor,
  AuthorFormGate,
  authorFormModalClosed,
  authorSubmitted,
} from './model/author-form.model'

export { authorFormModalOpened } from './model/author-form.model'

const authorSchema = z.object({
  nickname: z.string(),
  avatar: z.custom<UploadFileWithId>().nullable().optional(),
  description: z.string().nullable().optional(),
  contactInfo: z.string().nullable().optional(),
  church: z.string().nullable().optional(),
})

export type AuthorFormModel = z.infer<typeof authorSchema>

interface AuthorFormProps {
  isModal?: boolean
  onSubmitted?: (authorId: string) => void
}
export const AuthorForm = ({ isModal, onSubmitted }: AuthorFormProps) => {
  //   Queries
  const {
    data: authorData,
    pending: authorPending,
    error: authorError,
  } = useUnit(authorByIdQuery)

  const submitAuthor = useUnit(authorSubmitted)
  const submitting = useUnit($submittingAuthor)
  //   Modal
  const isModalOpened = useUnit($isAuthorFormModalOpened)
  const closeFormModal = useUnit(authorFormModalClosed)
  //   Form state
  const formik = useFormik<AuthorFormModel>({
    enableReinitialize: true,
    initialValues: useMemo(
      () =>
        authorData
          ? {
              nickname: authorData.nickname,
              avatar: authorData.avatar
                ? ({
                    id: authorData.avatar.id,
                    uid: authorData.avatar.id,
                    name: authorData.avatar.name,
                    status: 'done',
                    url: authorData.avatar.fullCdnUrl,
                  } as UploadFileWithId)
                : null,
              description: authorData.description,
              contactInfo: authorData.contactInfo,
              church: authorData.church,
            }
          : {
              nickname: '',
              avatar: null,
              description: null,
              contactInfo: null,
              church: null,
            },
      [authorData],
    ),
    validationSchema: toFormikValidationSchema(authorSchema),

    onSubmit: (values: AuthorFormModel, { setSubmitting }) => {
      if (values.avatar && values.avatar.status !== 'done') {
        void showNotificationWarningFx({
          message: 'Изображение ещё не загружено',
        })
      } else {
        const authorSubmittedData = getAuthorRequestByFormValues(values)
        submitAuthor(authorSubmittedData)
      }
      setSubmitting(false)
    },
  })

  useGate(AuthorFormGate, {
    onSubmitted: (id: string) => {
      if (onSubmitted) {
        onSubmitted(id)
      }
      if (isModal) {
        formik.resetForm()
      }
    },
    isModal,
  })

  const { isValid, isValidating, isSubmitting } = formik

  const isDirty = hasFormChanged(formik.values, formik.initialValues)

  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  // Prevent data lose when try to close

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])
  const tryToClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Данные не будут сохранены',
        content: 'Вы уверены, что хотите закрыть форму?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
    }
    formik.resetForm()
    closeFormModal()
  }

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  const formComponent = (
    <>
      <Form.Item layout="vertical">
        <UploadAvatar
          value={formik.values.avatar}
          onChange={(file) => {
            void formik.setFieldValue('avatar', file)
          }}
        />
      </Form.Item>
      <Form.Item layout="vertical" label="Псевдоним" required>
        <SelfValidateInput
          name="nickname"
          value={formik.values.nickname}
          showCount
          maxLength={MAX_FIELD_LENGTH.nickname}
          onChange={(value) => {
            void formik.setFieldValue('nickname', value)
          }}
        />
      </Form.Item>
      <Form.Item label="Описание" layout="vertical">
        <SelfValidateTextArea
          showCount
          maxLength={MAX_FIELD_LENGTH.description}
          value={formik.values.description ?? ''}
          onChange={(value) => {
            void formik.setFieldValue('description', value)
          }}
        />
      </Form.Item>
      <Form.Item label="Контактная информация" layout="vertical">
        <SelfValidateTextArea
          showCount
          maxLength={MAX_FIELD_LENGTH.contactInfo}
          value={formik.values.contactInfo ?? ''}
          onChange={(value) => {
            void formik.setFieldValue('contactInfo', value)
          }}
        />
      </Form.Item>
      <Form.Item layout="vertical" label="Церковь">
        <SelfValidateInput
          name="church"
          value={formik.values.church ?? ''}
          showCount
          maxLength={MAX_FIELD_LENGTH.church}
          onChange={(value) => {
            void formik.setFieldValue('church', value)
          }}
        />
      </Form.Item>
      <Flex justify="flex-end" gap={10}>
        <PrimaryButton
          disabled={formControlButtonsDisabled}
          onClick={() => {
            formik.handleSubmit()
          }}
          loading={submitting}
        >
          {authorData ? 'Сохранить' : 'Создать'}
        </PrimaryButton>
        {!authorData && (
          <Button
            htmlType="button"
            disabled={formControlButtonsDisabled}
            onClick={() => formik.resetForm()}
          >
            Очистить
          </Button>
        )}
      </Flex>
    </>
  )

  return (
    <>
      {isModal ? (
        <Modal
          open={isModalOpened}
          onCancel={() => {
            void tryToClose()
          }}
          destroyOnHidden
          centered
          title={
            authorData
              ? authorPending || (!authorData && !authorError)
                ? 'Загрузка'
                : authorData?.nickname
              : 'Создание исполнителя'
          }
          footer={null}
        >
          {formComponent}
        </Modal>
      ) : (
        formComponent
      )}
    </>
  )
}
